import { Box, Button } from "@mui/material";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import useAuth from "../../../hooks/useAuth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import { capitalizeFirstChar } from "../../../helpers/formatingFunctions";
import { useMsal } from "@azure/msal-react";
import { updateMoneyRequestFieldConfig } from "../../../formConfigs/Fleet/moneyRequestFieldConfig";
import { OptionType } from "../../../interfaces/Fleet/IGenericFormPopupProps";
import { fetchAllDrivers } from "../IncidentPopup/IncidentPopup";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";
import TruckPopup from "../TruckPopup/TruckPopup";
import DriverPopup from "../DriverPopup/DriverPopup";

const UpdateMoneyRequestPopup = ({
  moneyRequest,
  id,
}: {
  moneyRequest: any;
  id: string | undefined;
}) => {
  const [open, setOpen] = useState(false);
  const [driverOptions, setDriverOptions] = useState<OptionType[]>([]);
  const [truckOptions, setTruckOptions] = useState<OptionType[]>([]);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { accounts } = useMsal();
  const userName = accounts[0]?.username;

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSnackbarClose = () => setStatus("idle");

  const { token } = useAuth();

  const formatCapitalizedValue = (value?: string | null) =>
    value ? capitalizeFirstChar(value) : "";

  const formatPaymentMethod = (value?: string | null) => {
    if (!value) return "";
    return value.toLowerCase() === "efs"
      ? "EFS"
      : capitalizeFirstChar(value);
  };

  const baseInitialValues = useMemo(
    () => ({
      id,
      nameRequestedBy: moneyRequest?.nameRequestedBy ?? "",
      amount: moneyRequest?.amount ?? "",
      moneyRequestType: formatCapitalizedValue(moneyRequest?.moneyRequestType),
      paymentMethod: formatPaymentMethod(moneyRequest?.paymentMethod),
      description: moneyRequest?.description ?? "",
      issuedFor: formatCapitalizedValue(moneyRequest?.issuedFor),
      referenceId: moneyRequest?.referenceId ?? "",
    }),
    [
      id,
      moneyRequest?.amount,
      moneyRequest?.description,
      moneyRequest?.issuedFor,
      moneyRequest?.moneyRequestType,
      moneyRequest?.nameRequestedBy,
      moneyRequest?.paymentMethod,
      moneyRequest?.referenceId,
    ]
  );

  const [moneyRequestInitialValues, setMoneyRequestInitialValues] = useState<{
    [key: string]: any;
    truckId: OptionType | null;
    driverId: OptionType | null;
  }>(() => ({
    ...baseInitialValues,
    truckId: null,
    driverId: null,
  }));

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    string | undefined
  >(baseInitialValues.paymentMethod ?? "");

  const { data: allDrivers } = useQuery({
    queryKey: ["drivers"],
    queryFn: () => fetchAllDrivers(token),
    enabled: open && !!token,
  });

  const { data: allTrucks } = useQuery({
    queryKey: ["trucksTable"],
    queryFn: async () => {
      const res = await axios.get(`${apiVariable}/getAllTrucks`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { areActive: true },
      });
      return res.data.map((truck: any) => ({
        label: `${truck.unitNumber} | ${truck.make} ${truck.model} ${truck.year}`,
        id: truck.id.toString(),
      }));
    },
    enabled: open && !!token,
  });

  useEffect(() => {
    if (allDrivers) {
      setDriverOptions(allDrivers);
    }
  }, [allDrivers]);

  useEffect(() => {
    if (allTrucks) {
      setTruckOptions(allTrucks);
    }
  }, [allTrucks]);

  useEffect(() => {
    setMoneyRequestInitialValues((prev) => ({
      ...prev,
      ...baseInitialValues,
    }));
        setSelectedPaymentMethod(baseInitialValues.paymentMethod || "");
  }, [baseInitialValues]);

  useEffect(() => {
    if (!open) return;

    const driverId = moneyRequest?.driverId?.toString();
    let driverOption: OptionType | null = null;

    if (driverId) {
      driverOption = driverOptions.find((d) => d.id === driverId) ?? null;

      if (!driverOption && moneyRequest?.driverName) {
        driverOption = { id: driverId, label: moneyRequest.driverName };
      }
    } else if (moneyRequest?.driverName) {
      const nameLc = moneyRequest.driverName.toLowerCase();
      driverOption =
        driverOptions.find((d) => d.label.toLowerCase().startsWith(nameLc)) ??
        null;
      if (!driverOption) {
        driverOption = { id: "", label: moneyRequest.driverName };
      }
    }

    const truckId = moneyRequest?.truckId?.toString();
    let truckOption: OptionType | null = null;

    if (truckId) {
      truckOption = truckOptions.find((t) => t.id === truckId) ?? null;
      if (!truckOption && moneyRequest?.unitNumber) {
        truckOption = { id: truckId, label: moneyRequest.unitNumber };
      }
    } else if (moneyRequest?.unitNumber) {
      const unitLc = moneyRequest.unitNumber.toString().toLowerCase();
      truckOption =
        truckOptions.find((t) => t.label.toLowerCase().startsWith(unitLc)) ??
        null;
      if (!truckOption) {
        truckOption = { id: "", label: moneyRequest.unitNumber };
      }
    }

    setMoneyRequestInitialValues((prev) => ({
      ...baseInitialValues,
      driverId: prev.driverId ?? driverOption,
      truckId: prev.truckId ?? truckOption,
    }));
  }, [
    open,
    baseInitialValues,
    driverOptions,
    truckOptions,
    moneyRequest?.driverId,
    moneyRequest?.driverName,
    moneyRequest?.truckId,
    moneyRequest?.unitNumber,
  ]);
  const mutation = useMutation({
    mutationFn: (data: any) =>
      axios.put(`${apiVariable}/updateMoneyRequest`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    onSuccess: () => {
      setStatus("success");
      queryClient.invalidateQueries({ queryKey: ["moneyRequests", userName] });
      handleClose();
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response.data || "Failed to update money request");
    },
  });

const submitHandler = (data: any) => {
    data.usernameRequestedBy = userName;
    data.driverId = getId(data.driverId);
    data.truckId = getId(data.truckId);
    const paymentMethod = data.paymentMethod?.toLowerCase?.() ?? "";
    if (paymentMethod === "efs") {
      data.referenceId = data.referenceId?.trim();
      if (!data.referenceId) {
        data.referenceId = null;
      }
    } else {
      data.referenceId = null;
    }
    data.id = id;
    mutation.mutate(data);
  };

  const formFields = useMemo(
    () =>
      updateMoneyRequestFieldConfig.filter((field) => {
        if (field.name === "referenceId") {
          return selectedPaymentMethod?.toLowerCase?.() === "efs";
        }
        return true;
      }),
    [selectedPaymentMethod]
  );

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Update
      </Button>
      <GenericFormPopup
        formFields={formFields}
        initialValues={moneyRequestInitialValues}
        title="Update Money Request"
        onSubmit={submitHandler}
        open={open}
        onClose={handleClose}
        loading={mutation.isPending}
        autocompleteData={{ drivers: driverOptions, trucksTable: truckOptions }}
        onValuesChange={(values) => setSelectedPaymentMethod(values.paymentMethod)}
        fieldComponents={{
          truckId: (
            <Box display="flex" justifyContent="center" alignItems="center">
              <TruckPopup />
            </Box>
          ),
          driverId: (
            <Box display="flex" justifyContent="center" alignItems="center">
              <DriverPopup />
            </Box>
          ),
        }}
      />
      <StatusSnackbar
        status={status}
        successMessage={"Money Request updated successfully"}
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default UpdateMoneyRequestPopup;